import React, { useCallback, useEffect, useState } from 'react';
import Dropzone from 'react-dropzone';
import { makeStyles } from '@material-ui/core/styles';
import { AiOutlineCloudUpload } from 'react-icons/ai';
import { BaseHelperText } from 'src/components/common/BaseTextField/BaseHelperText/BaseHelperText';
import { FilePreviews } from './FilePreviews';
import { TUploaderFile, TUploaderFiles } from './types';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    border: `dashed 2px ${theme.palette.primary.main}`,
    padding: 0,
    margin: 0,
    height: '180px',
    width: '100%',
  },
  inputField: {
    display: 'none',
  },
  uploadIcon: {
    color: theme.palette.primary.main,
  },
}));

type PUploadFileButton = {
  onChooseFile: (files: (File | string)[]) => void;
  accept?: string;
  multiple?: boolean;
  error?: string;
  helperText?: string;
  enablePreview?: boolean;
  files?: TUploaderFile | TUploaderFiles;
  disabled?: boolean;
  onDelete?: () => void;
};

const getDragAndDropBorderStyles = (params: {
  isDragActive: boolean;
  isDragAccept: boolean;
  isDragReject: boolean;
}) => {
  const { isDragActive, isDragAccept, isDragReject } = params;

  const activeStyle = {
    borderColor: '#2196f3',
  };

  const acceptStyle = {
    borderColor: '#00e676',
  };

  const rejectStyle = {
    borderColor: '#ff1744',
  };

  return {
    cursor: 'pointer',
    ...(isDragActive ? activeStyle : {}),
    ...(isDragAccept ? acceptStyle : {}),
    ...(isDragReject ? rejectStyle : {}),
  };
};

const MemoizedFilesPreview = React.memo(FilePreviews);

export const FilesUploader = React.forwardRef<any, PUploadFileButton>(
  (props, ref) => {
    const {
      onChooseFile,
      accept = '',
      multiple = true,
      disabled = false,
      error,
      helperText,
      enablePreview = true,
      files,
      onDelete,
      ...btnProps
    } = props;

    const classes = useStyles();

    const dynamicStyles = error ? { border: 'dashed 2px red' } : {};

    const [localFiles, setLocalFiles] = useState<TUploaderFile>([]);

    useEffect(() => {
      if (files) {
        setLocalFiles(Array.isArray(files) ? files : [files]);
      }
    }, [files]);

    const setAllFiles = useCallback(
      (acceptedFiles: (File | string)[]) => {
        // Todo: Check why 'multiple' prop is not working
        let specificFiles;
        if (multiple) {
          specificFiles = acceptedFiles;
        } else {
          specificFiles = acceptedFiles.length
            ? [acceptedFiles[0]]
            : acceptedFiles;
        }

        setLocalFiles(specificFiles);
        onChooseFile(specificFiles);
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [multiple],
    );

    const handleDrop = (acceptedFiles: (File | string)[]) => {
      const newFiles = [...localFiles, ...Array.from(acceptedFiles)];
      setAllFiles(newFiles);
    };

    return (
      <>
        {disabled ? (
          <></>
        ) : (
          <Dropzone
            ref={ref}
            onDrop={handleDrop}
            accept={accept}
            multiple={multiple}>
            {({
              getRootProps,
              getInputProps,
              isDragActive,
              isDragAccept,
              isDragReject,
            }) => {
              return (
                <div
                  className={classes.root}
                  style={dynamicStyles}
                  {...getRootProps({
                    style: getDragAndDropBorderStyles({
                      isDragAccept,
                      isDragReject,
                      isDragActive,
                    }),
                  })}
                  {...btnProps}>
                  <input
                    type="file"
                    className={classes.inputField}
                    {...getInputProps()}
                  />
                  <>
                    <div style={{ minWidth: '40px', minHeight: '40px' }}>
                      <AiOutlineCloudUpload
                        className={classes.uploadIcon}
                        size="30"
                      />
                    </div>
                  </>
                  {helperText && (
                    <BaseHelperText error style={{ marginTop: '-15px' }}>
                      {helperText}
                    </BaseHelperText>
                  )}
                </div>
              );
            }}
          </Dropzone>
        )}
        {enablePreview && (
          <MemoizedFilesPreview
            files={localFiles}
            onRemove={setAllFiles}
            onDelete={onDelete}
          />
        )}
      </>
    );
  },
);

FilesUploader.displayName = 'FilesUploader';
